/**
 * AI Conversation Detail Component
 *
 * Displays full conversation details including:
 * - Call metadata
 * - Full transcript with timestamps
 * - Audio recording player
 * - AI-generated summary
 * - Intent and sentiment analysis
 * - Follow-up actions
 * - Function calls log
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import { useAuth } from '@/contexts/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  X,
  Phone,
  Clock,
  DollarSign,
  Mail,
  UserPlus,
  CheckCircle2,
  AlertCircle,
  Play,
  Pause,
  Volume2,
  Download,
  MessageSquare,
  Zap,
  TrendingUp,
  Loader2
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import type { AIConversation, AIMessage, AIFunctionCall } from '@/types/ai-support';
import { transformAIConversationFromDB } from '@/types/ai-support';
import { cn } from '@/lib/utils';

interface AIConversationDetailProps {
  conversationId: string;
  onClose: () => void;
}

const log = createComponentLogger('AIConversationDetail', 'ai-support');

export const AIConversationDetail: React.FC<AIConversationDetailProps> = ({
  conversationId,
  onClose
}) => {
  const { user } = useAuth();
  const { toast } = useToast();

  const [conversation, setConversation] = useState<AIConversation | null>(null);
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [functionCalls, setFunctionCalls] = useState<AIFunctionCall[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assigningFollowup, setAssigningFollowup] = useState(false);
  const [followupNotes, setFollowupNotes] = useState('');
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);

  useEffect(() => {
    loadConversation();
  }, [conversationId]);

  useEffect(() => {
    // Cleanup audio on unmount
    return () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.src = '';
      }
    };
  }, [audioElement]);

  const loadConversation = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load conversation
      const { data: convData, error: convError } = await supabase
        .from('ai_conversations')
        .select('*')
        .eq('id', conversationId)
        .single();

      if (convError) {
throw convError;
}

      const conv = transformAIConversationFromDB(convData);
      setConversation(conv);
      setFollowupNotes(conv.followupNotes || '');
      setSelectedUser(conv.assignedTo || '');

      // Load messages
      const { data: messagesData, error: messagesError } = await supabase
        .from('ai_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('turn_number', { ascending: true });

      if (!messagesError && messagesData) {
        setMessages(messagesData);
      }

      // Load function calls
      const { data: functionsData, error: functionsError } = await supabase
        .from('ai_function_calls')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('executed_at', { ascending: true });

      if (!functionsError && functionsData) {
        setFunctionCalls(functionsData);
      }

    } catch (err: any) {
      log.error('Error loading conversation', { conversationId }, err);
      setError(err.message || 'Failed to load conversation');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignFollowup = async () => {
    if (!conversation) {
return;
}

    try {
      setAssigningFollowup(true);

      const { error: updateError } = await supabase
        .from('ai_conversations')
        .update({
          requires_followup: true,
          followup_notes: followupNotes,
          assigned_to: selectedUser || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', conversationId);

      if (updateError) {
throw updateError;
}

      toast({
        title: 'Follow-up Assigned',
        description: 'Follow-up has been assigned successfully',
        icon: <CheckCircle2 className="h-4 w-4" />
      });

      loadConversation();

    } catch (err: any) {
      log.error('Error assigning follow-up', { conversationId, assignedTo }, err);
      toast({
        title: 'Assignment Failed',
        description: err.message || 'Failed to assign follow-up',
        variant: 'destructive'
      });
    } finally {
      setAssigningFollowup(false);
    }
  };

  const handleCompleteFollowup = async () => {
    if (!conversation) {
return;
}

    try {
      await supabase.rpc('mark_ai_followup_complete', {
        conversation_uuid: conversationId,
        completion_notes: followupNotes
      });

      toast({
        title: 'Follow-up Completed',
        description: 'Follow-up has been marked as complete',
        icon: <CheckCircle2 className="h-4 w-4" />
      });

      loadConversation();

    } catch (err: any) {
      log.error('Error completing follow-up', { conversationId }, err);
      toast({
        title: 'Error',
        description: err.message || 'Failed to complete follow-up',
        variant: 'destructive'
      });
    }
  };

  const handlePlayRecording = () => {
    if (!conversation?.recordingUrl) {
return;
}

    if (audioElement) {
      if (audioPlaying) {
        audioElement.pause();
        setAudioPlaying(false);
      } else {
        audioElement.play();
        setAudioPlaying(true);
      }
    } else {
      const audio = new Audio(conversation.recordingUrl);
      audio.onended = () => setAudioPlaying(false);
      audio.onerror = () => {
        setAudioPlaying(false);
        toast({
          title: 'Playback Error',
          description: 'Failed to play recording',
          variant: 'destructive'
        });
      };
      setAudioElement(audio);
      audio.play();
      setAudioPlaying(true);
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) {
return '0:00';
}
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getSentimentEmoji = (sentiment?: string) => {
    switch (sentiment) {
      case 'positive': return '😊';
      case 'negative': return '😟';
      default: return '😐';
    }
  };

  if (loading) {
    return (
      <Card className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </Card>
    );
  }

  if (error || !conversation) {
    return (
      <Card className="h-full">
        <CardContent className="pt-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error || 'Conversation not found'}</AlertDescription>
          </Alert>
          <Button onClick={onClose} variant="outline" className="mt-4">
            Close
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5" />
              {conversation.callerName || conversation.callerPhone}
            </CardTitle>
            <CardDescription className="mt-1">
              {format(new Date(conversation.callStartedAt), 'PPpp')} •{' '}
              {formatDistanceToNow(new Date(conversation.callStartedAt), { addSuffix: true })}
            </CardDescription>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Metadata Badges */}
        <div className="flex flex-wrap gap-2 mt-3">
          <Badge variant={conversation.callStatus === 'completed' ? 'default' : 'secondary'}>
            {conversation.callStatus}
          </Badge>
          {conversation.sentiment && (
            <Badge variant="outline">
              {getSentimentEmoji(conversation.sentiment)} {conversation.sentiment}
            </Badge>
          )}
          {conversation.intent && (
            <Badge variant="outline">
              <MessageSquare className="h-3 w-3 mr-1" />
              {conversation.intent}
            </Badge>
          )}
          {conversation.callDurationSeconds && (
            <Badge variant="outline">
              <Clock className="h-3 w-3 mr-1" />
              {formatDuration(conversation.callDurationSeconds)}
            </Badge>
          )}
          {conversation.costEstimateCents > 0 && (
            <Badge variant="outline">
              <DollarSign className="h-3 w-3 mr-1" />
              ${(conversation.costEstimateCents / 100).toFixed(2)}
            </Badge>
          )}
        </div>
      </CardHeader>

      <ScrollArea className="flex-1">
        <CardContent className="space-y-6">
          {/* AI Summary */}
          {conversation.summary && (
            <div className="space-y-2">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                AI Summary
              </Label>
              <div className="p-3 bg-muted/50 rounded-lg text-sm">
                {conversation.summary}
              </div>
            </div>
          )}

          {/* Key Points */}
          {conversation.keyPoints.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Key Points</Label>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                {conversation.keyPoints.map((point, idx) => (
                  <li key={idx}>{point}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Recording */}
          {conversation.recordingUrl && (
            <div className="space-y-2">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Volume2 className="h-4 w-4" />
                Recording
              </Label>
              <div className="flex items-center gap-2">
                <Button
                  onClick={handlePlayRecording}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  {audioPlaying ? <Pause className="h-4 w-4 mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                  {audioPlaying ? 'Pause' : 'Play'} Recording
                  {conversation.recordingDurationSeconds && (
                    <span className="ml-auto text-muted-foreground">
                      {formatDuration(conversation.recordingDurationSeconds)}
                    </span>
                  )}
                </Button>
                <Button variant="outline" size="icon" asChild>
                  <a href={conversation.recordingUrl} download>
                    <Download className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            </div>
          )}

          <Separator />

          {/* Transcript */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Conversation Transcript
            </Label>

            {conversation.fullTranscript.length > 0 ? (
              <div className="space-y-3">
                {conversation.fullTranscript.map((msg, idx) => (
                  <div
                    key={idx}
                    className={cn(
                      'flex gap-3 p-3 rounded-lg',
                      msg.role === 'user'
                        ? 'bg-blue-50 border-l-4 border-blue-500'
                        : 'bg-muted/50 border-l-4 border-primary'
                    )}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant={msg.role === 'user' ? 'secondary' : 'default'} className="text-xs">
                          {msg.role === 'user' ? 'Caller' : 'AI'}
                        </Badge>
                        {msg.timestamp && (
                          <span className="text-xs text-muted-foreground">
                            {formatDuration(Math.floor(msg.timestamp / 1000))}
                          </span>
                        )}
                      </div>
                      <p className="text-sm">{msg.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Alert>
                <AlertDescription>No transcript available</AlertDescription>
              </Alert>
            )}
          </div>

          {/* Function Calls */}
          {functionCalls.length > 0 && (
            <>
              <Separator />
              <div className="space-y-3">
                <Label className="text-sm font-semibold flex items-center gap-2">
                  <Zap className="h-4 w-4" />
                  Functions Called ({functionCalls.length})
                </Label>
                <div className="space-y-2">
                  {functionCalls.map((func) => (
                    <div key={func.id} className="p-3 border rounded-lg text-sm">
                      <div className="flex items-center justify-between mb-2">
                        <code className="font-mono text-xs bg-muted px-2 py-1 rounded">
                          {func.functionName}
                        </code>
                        <Badge variant={func.success ? 'default' : 'destructive'} className="text-xs">
                          {func.success ? 'Success' : 'Failed'}
                        </Badge>
                      </div>
                      {func.functionArgs && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-xs text-muted-foreground">
                            View details
                          </summary>
                          <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-auto">
                            {JSON.stringify(func.functionArgs, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Follow-up Actions */}
          <Separator />
          <div className="space-y-4">
            <Label className="text-sm font-semibold">Follow-up Actions</Label>

            {conversation.followupCompletedAt ? (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  Follow-up completed on {format(new Date(conversation.followupCompletedAt), 'PPp')}
                </AlertDescription>
              </Alert>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="followup-notes" className="text-xs">Notes</Label>
                  <Textarea
                    id="followup-notes"
                    value={followupNotes}
                    onChange={(e) => setFollowupNotes(e.target.value)}
                    placeholder="Add notes about this conversation..."
                    rows={3}
                  />
                </div>

                <div className="flex gap-2">
                  <Button onClick={handleAssignFollowup} disabled={assigningFollowup} className="flex-1">
                    <UserPlus className="h-4 w-4 mr-2" />
                    {conversation.requiresFollowup ? 'Update Follow-up' : 'Mark for Follow-up'}
                  </Button>
                  {conversation.requiresFollowup && (
                    <Button onClick={handleCompleteFollowup} variant="outline">
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Complete
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        </CardContent>
      </ScrollArea>
    </Card>
  );
};
